import { useCallback } from "react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import {
    SampleDataVersion,
    useBxpWebStargateClient
} from "@/libraries/bxp-web-stargate-client/src";

import { useSampleProjectConfiguration } from "../utils/sample-project";

import { useCloudStatus } from "./use-cloud-status";

export const useCreateSampleProject = () => {
    const { cloudId } = useCloudStatus();
    const stargateClient = useBxpWebStargateClient();
    const { teamType, projectDetails, sampleProjectConfiguration } =
        useSampleProjectConfiguration();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const createSampleProject = useCallback(async () => {
        try {
            const { projectId } = await stargateClient.sampleProjectCreate({
                cloudId,
                teamType: teamType,
                template: projectDetails.template,
                issueTypes: sampleProjectConfiguration.issueTypes,
                statusNames: sampleProjectConfiguration.columns,
                customDetail: {
                    sampleDataVersion: SampleDataVersion.MATURE,
                    enableExtraFields: true
                }
            });

            const promises: Promise<boolean>[] = [];

            const putSampleProjectUserPropertyPromise = stargateClient
                .putSampleProjectUserProperties({
                    projectId,
                    tenantId: cloudId,
                    template: projectDetails.template
                })
                .catch((error) => {
                    createAnalyticsEvent({
                        eventType: "track",
                        action: "failed",
                        actionSubject: "sampleProjectUserProperty",
                        error
                    }).fire();
                    return false;
                });

            promises.push(putSampleProjectUserPropertyPromise);

            await Promise.all(promises);

            createAnalyticsEvent({
                eventType: "track",
                action: "created",
                actionSubject: "createSampleProject"
            }).fire();

            return projectId;
        } catch (error) {
            console.error("Error creating sample project:", error);
            createAnalyticsEvent({
                eventType: "track",
                action: "failed",
                actionSubject: "createSampleProject",
                error
            }).fire();
        }

        return undefined;
    }, [
        stargateClient,
        cloudId,
        teamType,
        projectDetails,
        sampleProjectConfiguration
    ]);

    return { createSampleProject };
};
